import * as React from "react";
import type { PluginAvailability } from "./usePluginAvailability";
import { Field, ListEditor, NumberField, RowInput, SelectField, Toggle as KitToggle } from "./Form";
import type { CaptureSettings, SiteLogLevel } from "./SiteLogs";

// The per-site Caddy configuration — all of it.
//
// VhostOpts carries ~32 directives and v2 exposed exactly ONE of them (mtls), while v1's admin
// console has had the whole surface since it shipped: compression, WAF, rate limits, geo blocking,
// IP rules, custom headers, redirects, error pages, upstream timeouts, load balancing, SPA
// fallback. Every one of them was already implemented server-side and rendered into the site file.
// This is CLAUDE.md rule 8 at its plainest — a built capability nobody can reach — and it was
// invisible to the parity ratchet, which counts ROUTES reached, not fields exposed: one
// PUT /caddy/vhosts/{domain} covers all 32, so a screen offering one option scores the same as a
// screen offering every option.
//
// Grouping, wording and defaults are ported from v1 rather than reinvented: those tabs are what
// the operator already knows, and the placeholders ("private_ranges", "25MB", "5m") are the values
// the renderer actually accepts.
//
// PLUGIN INTERLOCK. rate_limit, cache, waf, brotli and geo are plugin-backed and render only when
// the plugin is in the running Caddy binary. A toggle for a plugin that is not built is a setting
// that reports itself on and does nothing — exactly the interlock rule 1 exists for — so those are
// labelled with what the binary actually has, from GET /caddy/plugins, and the save is still
// allowed (it activates if the plugin arrives) but never silently claimed to be in force.

export interface HeaderRule {
  op: "set" | "add" | "remove";
  name: string;
  value: string;
  /** Only on requests matching this path (e.g. /v1/sw.js); empty = the whole site. */
  path?: string;
}
export interface AppRoute {
  path: string;
  upstream: string;
  /** Remove the route's prefix before passing the request on: /v1/api/* → the app sees /users. */
  strip_prefix?: boolean;
  /** Wait this long (e.g. 15s) for the app while it restarts, instead of failing at once. */
  try_duration?: string;
}
/** Serve another path without the visitor's address changing. With `regexp`, `from` is a regular
 *  expression and `to` may use its groups as {re.1}, {re.2}… ; `user_agent` also requires the
 *  visitor's User-Agent to match. */
export interface RewriteRule {
  from: string;
  to: string;
  regexp?: boolean;
  user_agent?: string;
}

export interface CacheRule {
  extensions: string;
  max_age: string;
}

export interface AuthUser {
  name: string;
  /** bcrypt, from the server. Round-tripped unchanged; never displayed. */
  hash?: string;
  /** Only ever set when somebody types a new one. The server hashes and clears it. */
  password?: string;
}
export interface AuthArea {
  path: string;
  /** Paths inside the area served WITHOUT a login, each with everything under it. */
  except?: string[];
  users?: AuthUser[];
}

export interface RedirectRule {
  from: string;
  to: string;
  code: "permanent" | "temporary";
}
export interface VhostOptions {
  aliases?: string;
  forward_real_ip?: boolean;
  trusted_proxies?: string;
  compression?: boolean;
  security_headers?: boolean;
  max_body_size?: string;
  rate_limit?: boolean;
  rate_ip?: boolean;
  rate_ip_requests?: number;
  rate_ip_window?: string;
  rate_site?: boolean;
  rate_site_requests?: number;
  rate_site_window?: string;
  cache?: boolean;
  cache_ttl?: string;
  waf?: boolean;
  brotli?: boolean;
  geo?: boolean;
  geo_mode?: string;
  geo_countries?: string;
  streaming?: boolean;
  keepalive?: string;
  dial_timeout?: string;
  response_timeout?: string;
  lb_policy?: string;
  lb_try_duration?: string;
  fail_duration?: string;
  max_fails?: number;
  health_uri?: string;
  health_interval?: string;
  health_timeout?: string;
  health_status?: string;
  ip_mode?: string;
  /**
   * Password-protected directories — Caddy basic_auth, the .htaccess/.htpasswd equivalent.
   *
   * The server has carried this since vhosts.go was written and NO console rendered it. The
   * admin screen even knew: "the form must round-trip every field it was given, including the
   * ones it does not render: auth_areas, rewrites, app_routes" — so the code carefully preserved
   * a protected directory it gave nobody any way to create. It could only exist if somebody
   * called the API by hand.
   *
   * The credential contract, from hashAuth(): `password` is TRANSIENT input — the server runs
   * `caddy hash-password`, stores the bcrypt `hash`, and clears the plaintext. So a login that
   * is not being changed must be sent back with its HASH and an empty password, or it is dropped
   * as "no credential set". That is why the form round-trips hash and only ever sends a password
   * somebody just typed.
   */
  auth_areas?: AuthArea[];
  /** DirectoryIndex: which file answers a request for a directory. */
  index_files?: string;
  /** Options +Indexes: list a directory that has no index file. */
  browse?: boolean;
  /** The .htaccess habit of hiding dotfiles, .env, .git and vendor directories — which are
   *  otherwise served as PLAIN TEXT to anyone who guesses the path. */
  deny_paths?: string[];
  /** Turns OFF the default block of dotfiles, dumps, backup leftovers and keys (internal/sensitive). */
  allow_sensitive_paths?: boolean;
  /** ExpiresByType: Cache-Control per file extension. */
  cache_rules?: CacheRule[];
  // The Caddy vhost template emits the client_auth block ONLY when `mtls` is true. Without this
  // field + a toggle, enforcement could never be turned on from the console (the CA and certs are
  // built, but client_auth is never rendered). It saves with the full opts, so no partial-PUT wipe.
  mtls?: boolean;
  mtls_mode?: string;
  mtls_ca_path?: string;
  /** Send some paths of this site to a DIFFERENT backend — /api/* to one app, the rest to
   *  another. Distinct from a redirect: the visitor's address does not change. */
  app_routes?: AppRoute[];
  rewrites?: RewriteRule[];
  ip_list?: string;
  headers?: HeaderRule[];
  /** off | requests | headers | bodies — how much of each request the site records. */
  log_level?: SiteLogLevel;
  /** Body capture, meaningful only at log_level "bodies". The server fills the defaults and
   *  stamps who turned it on; an expired capture is dropped back to "headers" by the cluster. */
  capture?: CaptureSettings | null;
  redirect_www?: boolean;
  error_message?: string;
  error_page?: string;
  redirects?: RedirectRule[];
  spa_fallback?: boolean;
  [k: string]: unknown;
}

// Why each switch exists, in the operator's language. Ported from v1's OPT_HELP: a toggle whose
// consequence is not stated is a toggle nobody dares touch.
const HELP: Record<string, string> = {
  forward_real_ip: "Sends the visitor's real IP to your app as X-Real-IP. Without it the app sees the proxy's address, so logins, rate limits and logs all attribute every visitor to one address.",
  security_headers: "HSTS (force HTTPS), nosniff (no MIME guessing), frame protection (no embedding in other sites) and a referrer policy.",
  trusted_proxies: "Only trust forwarded-IP headers from these ranges. Set this when you sit behind Cloudflare or another proxy — otherwise a visitor can forge their own address.",
  max_body_size: "Largest upload accepted. Anything bigger is refused before it reaches your app.",
  compression: "gzip and zstd for text (HTML, CSS, JS, JSON). Usually cuts bandwidth substantially and costs almost nothing.",
  brotli: "Better compression than gzip for text, slightly more CPU.",
  cache: "Caches responses at the edge so repeat visitors never reach your app.",
  cache_ttl: "How long a cached response is reused before being fetched again.",
  waf: "Web Application Firewall using the OWASP rules — blocks common injection and scanning patterns before they reach your app.",
  rate_limit: "Master switch. Turn on one or both scopes below; they apply together.",
  rate_ip: "Each visitor gets their own budget — the usual choice, since it slows an attacker without affecting everyone else.",
  rate_site: "One budget shared by every visitor. This protects the server, but a single busy visitor can use it all up.",
  geo: "Allow or block visitors by the country their address belongs to.",
  ip_mode: "Allow only the listed addresses, or block them. Anything not covered by the list goes the other way.",
  ip_list: "Addresses or ranges, comma or space separated. 'private_ranges' covers all internal networks.",
  streaming: "Sends each chunk to the visitor immediately instead of buffering. Needed for server-sent events, live logs and streaming responses.",
  keepalive: "How long an idle connection to your app is kept open for reuse.",
  dial_timeout: "How long to wait when opening a connection to your app before giving up.",
  response_timeout: "How long to wait for your app to start responding before returning an error.",
  lb_policy: "How requests are shared across multiple upstreams. Only applies when more than one is listed.",
  health_uri: "A path probed periodically; an upstream that fails it stops receiving traffic until it recovers.",
  log_level: "How much of each request this site records, for the log review. Bandwidth is counted separately (from the web server's metrics) at every level, “off” included.",
  redirect_www: "Sends the other form of the name (www or bare) to this one, so a page has a single address rather than two.",
  error_message: "Shown with the status code on any error page (404, 502, …).",
  error_page: "A file in the site root served instead of the built-in error page, e.g. /404.html.",
  spa_fallback: "Serves index.html for any path that is not a real file, which is what a single-page app needs for its own routing to work.",
};

const WINDOWS = ["10s", "1m", "5m", "1h"];

interface Props {
  opts: VhostOptions;
  /** key -> whether the plugin is in the RUNNING Caddy binary, from GET /caddy/plugins. */
  pluginAvailable?: PluginAvailability;
  saving?: boolean;
  onSave: (opts: VhostOptions) => void;
  /** Reverse-proxy-only sections are pointless for a static site. */
  isProxy?: boolean;
  /** Which tab to open on. Lets a deep link (e.g. "secure this site" from an app) land straight on
   *  Password protection instead of the default General tab. */
  initialTab?: TabKey;
}

export type TabKey = "protect" | "general" | "security" | "performance" | "headers" | "redirects" | "access";

export function VhostOptionsForm({ opts, pluginAvailable, saving, onSave, isProxy = true, initialTab }: Props) {
  const [draft, setDraft] = React.useState<VhostOptions>(opts);
  const [tab, setTab] = React.useState<TabKey>(initialTab ?? "general");
  const [secTab, setSecTab] = React.useState<"firewall" | "rate" | "geo">("firewall");

  // The draft follows the server whenever the server's copy changes — after a save, or when
  // another admin changed it — without discarding edits in progress on the same values.
  React.useEffect(() => setDraft(opts), [opts]);

  const set = <K extends keyof VhostOptions>(k: K, v: VhostOptions[K]) => setDraft((d) => ({ ...d, [k]: v }));
  const dirty = JSON.stringify(draft) !== JSON.stringify(opts);

  // Bound to the draft by key, and DELEGATING to the kit primitive for the markup. The local
  // wrapper earns its place by binding state and pulling HELP from one table; it does not get to
  // have its own opinion about what an input looks like, which is how 76 copies of the same
  // className appeared across the consoles.
  const Toggle = ({ k, label, plugin }: { k: keyof VhostOptions; label: string; plugin?: boolean }) => {
    const avail = pluginAvailable?.[String(k)];
    // true: in the running build; false: not in it; null/absent: could not be checked — said so,
    // never shown as "in build".
    const built = !plugin || avail === true;
    const unknown = !!plugin && avail == null;
    return (
      <KitToggle
        checked={!!draft[k]}
        onChange={(b) => set(k, b as never)}
        help={HELP[String(k)]}
        label={
          <>
            {label}
            {plugin && (
              <span className={`ml-2 rounded px-1.5 py-0.5 text-[10.5px] ${built ? "bg-oksoft text-ok" : unknown ? "bg-surface2 text-muted" : "bg-warnsoft text-warn"}`}>
                {built ? "in build" : unknown ? "not checked" : "needs build"}
              </span>
            )}
          </>
        }
      />
    );
  };

  const Text = ({ k, label, ph }: { k: keyof VhostOptions; label: string; ph?: string }) => (
    <div className="py-1.5">
      <Field
        label={label}
        value={String(draft[k] ?? "")}
        placeholder={ph}
        onChange={(x) => set(k, x as never)}
        help={HELP[String(k)]}
      />
    </div>
  );

  // Inline numeric + window pickers for the rate-limit row, where the controls sit in a sentence
  // rather than in a labelled column. RowInput carries the shared input styling.
  const Num = ({ k, def }: { k: keyof VhostOptions; def: number }) => (
    <span className="inline-block w-24 align-middle">
      <RowInput
        value={String(draft[k] ?? def)}
        onChange={(v) => set(k, (parseInt(v, 10) || 0) as never)}
      />
    </span>
  );

  const Win = ({ k, def }: { k: keyof VhostOptions; def: string }) => (
    <select
      value={String(draft[k] ?? def)}
      onChange={(e) => set(k, e.target.value as never)}
      className="rounded-lg border border-line2 bg-bg px-2 py-1 text-[13px] text-fg outline-none focus:border-accent"
    >
      {WINDOWS.map((w) => (
        <option key={w} value={w}>{w}</option>
      ))}
    </select>
  );

  const Group = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <div className="min-w-0 flex-1">
      <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-muted">{title}</div>
      {children}
    </div>
  );

  const tabs: [TabKey, string][] = [
    ["general", "General"],
    ["security", "Security"],
    ["performance", "Performance"],
    ["headers", "Headers"],
    ["redirects", "Redirects & errors"],
    ["protect", "Password protection"],
    ["access", "Access & logs"],
  ];

  const rules: HeaderRule[] = draft.headers ?? [];
  const redirects: RedirectRule[] = draft.redirects ?? [];
  const areas: AuthArea[] = draft.auth_areas ?? [];

  return (
    <div>
      <div className="mb-4 flex flex-wrap gap-1 border-b border-line">
        {tabs.map(([t, label]) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={`-mb-px border-b-2 px-3 py-2 text-[12.5px] ${tab === t ? "border-accent text-fg" : "border-transparent text-muted hover:text-fg"}`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "general" && (
        <div className="flex flex-col gap-6 sm:flex-row">
          <Group title="Client & headers">
            <Toggle k="forward_real_ip" label="Forward the visitor's IP to your app" />
            <Toggle k="security_headers" label="Security headers (HSTS, nosniff, frame, referrer)" />
            <Text k="trusted_proxies" label="Trusted proxies" ph="private_ranges or CIDRs" />
            <Text k="max_body_size" label="Largest upload" ph="25MB" />
            <Text k="aliases" label="Other names this site answers for" ph="www.example.com mail.example.com" />
          </Group>
          {!isProxy && (
            <Group title="Files & directories">
              {/* The .htaccess trio, and the server's own comments name them as such:
                  index_files is DirectoryIndex, browse is Options +Indexes, deny_paths is the
                  habit of hiding dotfiles. All three were in the request body and in no form. */}
              <Text k="index_files" label="Directory index" ph="index.php index.html" />
              <Toggle k="browse" label="List the files when a directory has no index" />
              <div className="mt-2">
                <div className="text-[12px] text-fg">Blocked paths</div>
                <div className="mb-1 text-[11.5px] text-muted">
                  Never served, whatever else matches. Without these, files like{" "}
                  <span className="font-mono">.env</span> and <span className="font-mono">.git</span>{" "}
                  are delivered as plain text to anyone who guesses the name.
                </div>
                <ListEditor
                  items={draft.deny_paths ?? []}
                  onChange={(v) => set("deny_paths", v)}
                  blank={() => ""}
                  addLabel="+ block a path"
                  row={(item, setItem) => (
                    <RowInput value={item} placeholder="/.env  or  /.git/*" onChange={setItem} />
                  )}
                />
              </div>
            </Group>
          )}
          {isProxy && (
            <Group title="Connection to your app">
              <Toggle k="streaming" label="Streaming / server-sent events (send immediately)" />
              <Text k="keepalive" label="Keep-alive" ph="30s" />
              <Text k="dial_timeout" label="Connect timeout" ph="10s" />
              <Text k="response_timeout" label="Response timeout" ph="30s" />
            </Group>
          )}
        </div>
      )}

      {tab === "security" && (
        <div>
          <div className="mb-3 flex flex-wrap gap-1">
            {([["firewall", "Firewall & IP"], ["rate", "Rate limits"], ["geo", "Geo blocking"]] as const).map(([t, l]) => (
              <button
                key={t}
                type="button"
                onClick={() => setSecTab(t)}
                className={`rounded-full px-3 py-1 text-[12px] ${secTab === t ? "bg-accentsoft text-accent" : "text-muted hover:text-fg"}`}
              >
                {l}
              </button>
            ))}
          </div>

          {secTab === "firewall" && (
            <div className="max-w-xl">
              <Toggle k="waf" label="Web Application Firewall (OWASP rules)" plugin />
              {/* allow_sensitive_paths turns OFF the block every site gets by default (templates/caddy/
                  vhost.caddy.tmpl, the list in internal/sensitive). Worded as what it EXPOSES, because the
                  switch removes a protection rather than adding a feature. */}
              <div className="mt-4 text-[11px] font-medium uppercase tracking-wide text-muted">Sensitive files</div>
              <KitToggle
                checked={!!draft.allow_sensitive_paths}
                onChange={(b) => set("allow_sensitive_paths", b)}
                label="Serve sensitive files — turn OFF the built-in block"
                help={
                  "Every site refuses (404) dotfiles and dot folders such as .env, .git/ and .htpasswd (/.well-known/ excepted), " +
                  "database dumps and files (.sql, .sqlite, .db, .dump), backup and editor leftovers (.bak, .old, .orig, .swp, ~) " +
                  "and private keys (.pem, .key, .p12, .pfx). Leave this off unless the site genuinely has to hand one of these out."
                }
                consequence={
                  draft.allow_sensitive_paths
                    ? "ON: any of those files inside the site's folder is served to anyone who guesses its name — passwords in an .env, a whole database in a dump, a private key. The exposure scanner reports each one it finds; move them outside the folder instead where you can."
                    : undefined
                }
              />
              <div className="mt-4 text-[11px] font-medium uppercase tracking-wide text-muted">Who may reach this site</div>
              <label className="block py-1.5 text-[12px] text-muted">
                Mode
                <select
                  value={String(draft.ip_mode ?? "")}
                  onChange={(e) => set("ip_mode", e.target.value)}
                  className="mt-1 w-full rounded-lg border border-line2 bg-bg px-3 py-1.5 text-[13px] text-fg outline-none focus:border-accent"
                >
                  <option value="">off — everyone</option>
                  <option value="allow">only these addresses</option>
                  <option value="deny">everyone except these</option>
                </select>
                <span className="mt-1 block text-[11.5px]">{HELP.ip_mode}</span>
              </label>
              {draft.ip_mode ? <Text k="ip_list" label="Addresses / ranges" ph="203.0.113.0/24, 198.51.100.5" /> : null}

              {/* Client certificates. `mtls` is the ON switch — the template renders client_auth
                  only when it is true; `mtls_mode` decides HOW strict, and `mtls_ca_path` which CA
                  is trusted (auto-set by "generate CA" on the Client-certificates tab). Enforcement
                  had NO on switch in this form before, so it could never be turned on. */}
              <div className="mt-4 text-[11px] font-medium uppercase tracking-wide text-muted">Client certificates</div>
              <Toggle k="mtls" label="Require a client certificate to connect" />
              <label className="block py-1.5 text-[12px] text-muted">
                Requirement
                <select
                  value={String(draft.mtls_mode ?? "")}
                  onChange={(e) => set("mtls_mode", e.target.value)}
                  className="mt-1 w-full rounded-lg border border-line2 bg-bg px-3 py-1.5 text-[13px] text-fg outline-none focus:border-accent"
                >
                  <option value="">not set</option>
                  <option value="request">ask, accept anyone</option>
                  <option value="require">demand one, do not check it</option>
                  <option value="verify_if_given">check it if one is offered</option>
                  <option value="require_and_verify">demand one and check it</option>
                </select>
                <span className="mt-1 block text-[11.5px]">
                  Only the last option actually keeps anyone out. The middle two accept a
                  certificate without establishing that it is one you issued.
                </span>
              </label>
              <Text k="mtls_ca_path" label="Trusted client CA (PEM)" ph="/etc/ozpanel/clientca/example.com.pem" />
            </div>
          )}

          {secTab === "rate" && (
            <div className="max-w-xl">
              <Toggle k="rate_limit" label="Rate limiting" plugin />
              <div className="mt-2 space-y-2 border-l-2 border-line pl-4">
                <Toggle k="rate_ip" label="Per visitor" />
                <div className="flex items-center gap-2 text-[12.5px] text-muted">
                  <Num k="rate_ip_requests" def={100} /> requests per <Win k="rate_ip_window" def="1m" />
                </div>
                <Toggle k="rate_site" label="Whole site" />
                <div className="flex items-center gap-2 text-[12.5px] text-muted">
                  <Num k="rate_site_requests" def={10000} /> requests per <Win k="rate_site_window" def="1m" />
                </div>
              </div>
            </div>
          )}

          {secTab === "geo" && (
            <div className="max-w-xl">
              <Toggle k="geo" label="Geo blocking" plugin />
              <label className="block py-1.5 text-[12px] text-muted">
                Mode
                <select
                  value={String(draft.geo_mode ?? "block")}
                  onChange={(e) => set("geo_mode", e.target.value)}
                  className="mt-1 w-full rounded-lg border border-line2 bg-bg px-3 py-1.5 text-[13px] text-fg outline-none focus:border-accent"
                >
                  <option value="block">Block the listed countries</option>
                  <option value="allow">Allow only the listed countries</option>
                </select>
              </label>
              <Text k="geo_countries" label="Countries" ph="CN, RU" />
            </div>
          )}
        </div>
      )}

      {tab === "performance" && (
        <div className="max-w-xl">
          <Toggle k="compression" label="Compression (gzip + zstd)" />
          <Toggle k="brotli" label="Brotli compression" plugin />
          <Toggle k="cache" label="Cache responses at the edge" plugin />
          <Text k="cache_ttl" label="Cache for" ph="5m" />
          {/* ExpiresByType. Static assets are the reason anyone sets this, so the placeholders
              are the assets rather than an abstract example. */}
          <div className="mt-4 text-[11px] font-medium uppercase tracking-wide text-muted">How long browsers keep files</div>
          <div className="mb-1 text-[12px] text-muted">
            Per file type, so images and stylesheets are not re-fetched on every page view.
          </div>
          <ListEditor
            items={draft.cache_rules ?? []}
            onChange={(v) => set("cache_rules", v)}
            blank={() => ({ extensions: "", max_age: "" })}
            addLabel="+ add a rule"
            row={(r, setR) => (
              <>
                <RowInput value={r.extensions} placeholder=".css .js .woff2" onChange={(v) => setR({ ...r, extensions: v })} />
                <RowInput value={r.max_age} placeholder="30d" width="w-24" onChange={(v) => setR({ ...r, max_age: v })} />
              </>
            )}
          />
          {isProxy && (
            <>
              <div className="mt-4 text-[11px] font-medium uppercase tracking-wide text-muted">Multiple upstreams</div>
              <label className="block py-1.5 text-[12px] text-muted">
                Sharing
                <select
                  value={String(draft.lb_policy ?? "")}
                  onChange={(e) => set("lb_policy", e.target.value)}
                  className="mt-1 w-full rounded-lg border border-line2 bg-bg px-3 py-1.5 text-[13px] text-fg outline-none focus:border-accent"
                >
                  <option value="">default (round robin)</option>
                  <option value="round_robin">round robin</option>
                  <option value="least_conn">fewest connections</option>
                  <option value="ip_hash">same visitor to same upstream</option>
                  <option value="random">random</option>
                  <option value="first">first that is up</option>
                </select>
                <span className="mt-1 block text-[11.5px]">{HELP.lb_policy}</span>
              </label>
              <Text k="health_uri" label="Health check path" ph="/health" />
              <div className="flex gap-2">
                <Text k="health_interval" label="Every" ph="10s" />
                <Text k="health_status" label="Expect" ph="200" />
              </div>
            </>
          )}
        </div>
      )}

      {tab === "headers" && (
        <div className="max-w-3xl">
          <div className="mb-2 text-[12px] text-muted">
            Add, override or remove headers Caddy sends to visitors — e.g. set{" "}
            <span className="font-mono">Cache-Control: no-store</span>, or remove <span className="font-mono">Server</span>.
          </div>
          <ListEditor
            items={rules}
            onChange={(v) => set("headers", v)}
            blank={(): HeaderRule => ({ op: "set", name: "", value: "" })}
            addLabel="+ add header"
            emptyMessage="No custom headers."
            row={(h, setH) => (
              <>
                <select
                  value={h.op}
                  onChange={(e) => setH({ ...h, op: e.target.value as HeaderRule["op"] })}
                  className="w-24 rounded-lg border border-line2 bg-bg px-2 py-1.5 text-[13px] text-fg"
                >
                  <option value="set">set</option>
                  <option value="add">add</option>
                  <option value="remove">remove</option>
                </select>
                <RowInput value={h.name} placeholder="Header-Name" onChange={(v) => setH({ ...h, name: v })} />
                {/* A "remove" rule has no value, and the box says so by being unusable rather
                    than by accepting a value that is silently dropped. */}
                {h.op === "remove" ? (
                  <span className="min-w-[8rem] flex-1 text-[11.5px] text-muted">no value — the header is stripped</span>
                ) : (
                  <RowInput value={h.value} placeholder="value" onChange={(v) => setH({ ...h, value: v })} />
                )}
                <RowInput value={h.path ?? ""} placeholder="only on path (optional)" onChange={(v) => setH({ ...h, path: v })} />
              </>
            )}
          />
        </div>
      )}

      {tab === "redirects" && (
        <div className="max-w-3xl">
          <Toggle k="redirect_www" label="Send the other www/apex form to this one" />
          <div className="mt-4 text-[11px] font-medium uppercase tracking-wide text-muted">Custom redirects</div>
          <div className="mb-2 text-[12px] text-muted">
            Send visitors from one path to another path or URL — the address in their browser changes.
          </div>
          <ListEditor
            items={redirects}
            onChange={(v) => set("redirects", v)}
            blank={() => ({ from: "", to: "", code: "permanent" as RedirectRule["code"] })}
            addLabel="+ add redirect"
            emptyMessage="No redirects."
            row={(r, setR) => (
              <>
                <RowInput value={r.from} placeholder="/old  or  /old/*" onChange={(v) => setR({ ...r, from: v })} />
                <span className="text-muted">&rarr;</span>
                <RowInput value={r.to} placeholder="/  or  https://example.com{uri}" onChange={(v) => setR({ ...r, to: v })} />
                <select
                  value={r.code}
                  onChange={(e) => setR({ ...r, code: e.target.value as RedirectRule["code"] })}
                  className="rounded-lg border border-line2 bg-bg px-2 py-1.5 text-[12.5px] text-fg"
                >
                  <option value="permanent">301 permanent</option>
                  <option value="temporary">302 temporary</option>
                </select>
              </>
            )}
          />

          {/* Path → upstream. It sat in the request body with no field anywhere, so a site could
              be served by exactly one backend: splitting /api off to a second app was declarable
              in the API and unreachable from every console. Unlike a redirect the address bar does
              not change — the visitor never sees that a different app answered. */}
          <div className="mt-5 text-[11px] font-medium uppercase tracking-wide text-muted">Send some paths to another app</div>
          <div className="mb-2 text-[12px] text-muted">
            Everything not listed here is served by the site&apos;s own backend.
          </div>
          <ListEditor
            items={draft.app_routes ?? []}
            onChange={(v) => set("app_routes", v)}
            blank={(): AppRoute => ({ path: "", upstream: "" })}
            addLabel="+ send a path elsewhere"
            row={(a, setA) => (
              <>
                <RowInput value={a.path} placeholder="/api/*" onChange={(v) => setA({ ...a, path: v })} />
                <span className="text-muted">&rarr;</span>
                <RowInput value={a.upstream} placeholder="127.0.0.1:8080" onChange={(v) => setA({ ...a, upstream: v })} />
                <label className="flex items-center gap-1.5 whitespace-nowrap text-[12px] text-muted" title="Remove the path's prefix before passing it on: /v1/api/users reaches the app as /users. The path must end in /*.">
                  <input type="checkbox" checked={!!a.strip_prefix} onChange={(e) => setA({ ...a, strip_prefix: e.target.checked })} />
                  strip prefix
                </label>
                <RowInput value={a.try_duration ?? ""} placeholder="wait while restarting, e.g. 15s" onChange={(v) => setA({ ...a, try_duration: v })} />
              </>
            )}
          />

          {/* Rewrites were in the API from the start and no console rendered them: a site could carry
              one only if somebody called the API by hand. */}
          <div className="mt-5 text-[11px] font-medium uppercase tracking-wide text-muted">Rewrites</div>
          <div className="mb-2 text-[12px] text-muted">
            Serve another path without the visitor&apos;s address changing; the query string is kept. With
            &ldquo;regular expression&rdquo; the first box is a pattern such as{" "}
            <span className="font-mono">^/for/([^/]+)$</span> and the target can use its parts as{" "}
            <span className="font-mono">{"{re.1}"}</span>. Write <span className="font-mono">[0-9]</span> rather than a backslash.
            A user agent pattern limits the rule to matching visitors (e.g. link-preview bots).
          </div>
          <ListEditor
            items={draft.rewrites ?? []}
            onChange={(v) => set("rewrites", v)}
            blank={(): RewriteRule => ({ from: "", to: "" })}
            addLabel="+ add rewrite"
            emptyMessage="No rewrites."
            row={(w, setW) => (
              <>
                <RowInput value={w.from} placeholder={w.regexp ? "^/business/(.*)$" : "/business/*"} onChange={(v) => setW({ ...w, from: v })} />
                <span className="text-muted">&rarr;</span>
                <RowInput value={w.to} placeholder={w.regexp ? "/v1/site/page/{re.1}" : "/index.php?{query}"} onChange={(v) => setW({ ...w, to: v })} />
                <label className="flex items-center gap-1.5 whitespace-nowrap text-[12px] text-muted">
                  <input type="checkbox" checked={!!w.regexp} onChange={(e) => setW({ ...w, regexp: e.target.checked })} />
                  regular expression
                </label>
                <RowInput value={w.user_agent ?? ""} placeholder="user agent pattern (optional)" onChange={(v) => setW({ ...w, user_agent: v })} />
              </>
            )}
          />

          <div className="mt-5 text-[11px] font-medium uppercase tracking-wide text-muted">When something goes wrong</div>
          <Text k="error_message" label="Error message" ph="Something went wrong — please try again." />
          <Text k="error_page" label="Error page file" ph="/404.html" />
          <Toggle k="spa_fallback" label="Single-page app: serve index.html for unknown paths" />
        </div>
      )}

      {tab === "protect" && (
        <div className="max-w-3xl">
          <div className="mb-2 text-[12px] leading-relaxed text-muted">
            Ask for a username and password before anything under a path is served — the
            .htaccess equivalent. Each area is independent and matches by prefix, so
            <span className="font-mono"> /admin</span> also covers
            <span className="font-mono"> /admin/settings</span>. Protect
            <span className="font-mono"> /</span> to lock the whole site, then list the paths that stay
            open (an API, a shop, legal pages, <span className="font-mono">/sw.js</span>).
          </div>
          {/* A list inside a list: each area has its own logins. `boxed` is what makes the outer
              rows readable when the inner one is itself a list. */}
          <ListEditor
            boxed
            items={areas}
            onChange={(v) => set("auth_areas", v)}
            blank={() => ({ path: "", users: [{ name: "", password: "" }] })}
            addLabel="+ protect a directory"
            emptyMessage="Nothing is password-protected."
            row={(a, setA) => (
              <>
                <RowInput value={a.path} placeholder="/admin" onChange={(v) => setA({ ...a, path: v })} />
                <div className="mt-2">
                  <div className="text-[12px] text-fg">Open without a login</div>
                  <div className="mb-1 text-[11.5px] text-muted">
                    Each path and everything under it is served to anyone. Use{" "}
                    <span className="font-mono">*</span> for a pattern, e.g.{" "}
                    <span className="font-mono">/*.webmanifest</span>.
                  </div>
                  <ListEditor
                    items={a.except ?? []}
                    onChange={(except) => setA({ ...a, except })}
                    blank={() => ""}
                    addLabel="+ leave a path open"
                    row={(item, setItem) => (
                      <RowInput value={item} placeholder="/api  or  /sw.js" onChange={setItem} />
                    )}
                  />
                </div>
                <div className="mt-2">
                  <ListEditor
                    items={a.users ?? []}
                    onChange={(users) => setA({ ...a, users })}
                    blank={() => ({ name: "", password: "" })}
                    addLabel="+ add login"
                    row={(u, setU) => (
                      <>
                        <RowInput value={u.name} placeholder="username" mono={false} width="w-40" onChange={(v) => setU({ ...u, name: v })} />
                        <RowInput
                          type="password"
                          mono={false}
                          width="w-44"
                          value={u.password ?? ""}
                          placeholder={u.hash ? "unchanged" : "password"}
                          onChange={(v) => setU({ ...u, password: v })}
                        />
                        {/* An EXISTING login shows "password kept" rather than a row of dots. The
                            server only ever returns the bcrypt hash, so dots would be a lie about
                            what is held — and leaving the box empty is exactly how you keep it. */}
                        {u.hash && !u.password && <span className="text-[11.5px] text-muted">password kept</span>}
                      </>
                    )}
                  />
                </div>
              </>
            )}
          />
          <p className="mt-3 text-[11.5px] leading-relaxed text-muted">
            A login with no password and no existing one is dropped rather than saved as a broken
            entry — an area that cannot authenticate anybody would otherwise lock the directory
            for everyone.
          </p>
        </div>
      )}

      {tab === "access" && (
        <LoggingOptions
          level={(draft.log_level ?? "off") as SiteLogLevel}
          capture={draft.capture ?? null}
          saved={opts}
          captureBuilt={pluginAvailable?.capture !== false}
          onChange={(level, capture) => setDraft((d) => ({ ...d, log_level: level, capture }))}
        />
      )}

      <div className="mt-5 flex items-center gap-3 border-t border-line pt-4">
        <button
          type="button"
          disabled={!dirty || saving}
          onClick={() => onSave(draft)}
          className="rounded-lg bg-accent px-3.5 py-1.5 text-[13px] font-medium text-onaccent disabled:opacity-40"
        >
          {saving ? "Saving…" : "Save changes"}
        </button>
        {dirty && (
          <button type="button" className="text-[12.5px] text-muted hover:text-fg" onClick={() => setDraft(opts)}>
            Discard
          </button>
        )}
        <span className="text-[11.5px] text-muted">
          Checked and reloaded on save — an invalid setting is refused rather than taking the site down.
        </span>
      </div>
    </div>
  );
}

const LEVELS: { value: SiteLogLevel; label: string; help: string }[] = [
  { value: "off", label: "Off", help: "No requests are recorded. Bandwidth is still counted." },
  { value: "requests", label: "Requests", help: "Method, path, status, size, time taken and client address for every request. No headers." },
  { value: "headers", label: "Requests + headers", help: "Also the request and response headers, exactly as sent — cookies and Authorization included." },
  {
    value: "bodies",
    label: "Requests + headers + bodies",
    help: "Also records request and/or response BODIES for a limited time — for debugging a form, an API client or a webhook. Recorded exactly as sent, nothing redacted.",
  },
];

const CAPTURE_METHODS = ["GET", "HEAD", "POST", "PUT", "PATCH", "DELETE", "OPTIONS"];
const DURATIONS: [string, number][] = [
  ["1 hour", 1],
  ["6 hours", 6],
  ["24 hours", 24],
  ["3 days", 72],
  ["7 days", 168],
];

// The logging tab. The expiry is an ABSOLUTE time on the server (capture.until); the form offers a
// duration and turns it into one at the moment it is chosen. An unchanged capture keeps its saved
// expiry — re-saving an unrelated setting must not silently extend a capture somebody else started.
function LoggingOptions({
  level,
  capture,
  saved,
  captureBuilt,
  onChange,
}: {
  level: SiteLogLevel;
  capture: CaptureSettings | null;
  saved: VhostOptions;
  captureBuilt: boolean;
  onChange: (level: SiteLogLevel, capture: CaptureSettings | null) => void;
}) {
  const c: CaptureSettings = capture ?? {};
  const setC = (patch: Partial<CaptureSettings>) => onChange(level, { ...c, ...patch });
  const now = Math.floor(Date.now() / 1000);
  const savedCap = saved.log_level === "bodies" ? saved.capture : null;
  const running = !!savedCap?.until && savedCap.until > now;
  const methods = c.methods && c.methods.length ? c.methods : ["POST", "PUT", "PATCH", "DELETE"];
  // "" keeps the expiry already set; a duration sets a new one from the moment it is picked.
  const [dur, setDur] = React.useState("");

  const setLevel = (l: string) => {
    const next = l as SiteLogLevel;
    setDur("");
    if (next !== "bodies") return onChange(next, null);
    // Turning capture on: the server's defaults, with an explicit 24 h expiry the operator can see.
    onChange(next, capture ?? { request: true, response: false, methods, max_kb: 64, retention_hours: 24, until: now + 24 * 3600 });
  };

  return (
    <div className="flex max-w-2xl flex-col gap-4">
      <SelectField
        label="Logging level"
        value={level}
        onChange={setLevel}
        options={LEVELS}
        help={HELP.log_level}
        consequence={
          level === "off"
            ? "With logging off this site's requests cannot be reviewed."
            : undefined
        }
      />

      {level === "bodies" && (
        <div className="rounded-lg border border-line p-4">
          <div className="mb-2 text-[11px] font-medium uppercase tracking-wide text-muted">Body capture</div>
          {!captureBuilt && (
            <p className="mb-3 text-[12px] text-crit">
              The running web server was built without the body-capture module, so this setting would record nothing.
            </p>
          )}
          {running && savedCap && (
            <p className="mb-3 text-[12px] text-warn">
              Capturing now, until {new Date(savedCap.until! * 1000).toLocaleString()}
              {savedCap.enabled_by ? ` — turned on by ${savedCap.enabled_by}` : ""}
              {savedCap.enabled_at ? ` at ${new Date(savedCap.enabled_at * 1000).toLocaleString()}` : ""}.
            </p>
          )}
          {!running && savedCap && (
            <p className="mb-3 text-[12px] text-muted">
              The last capture ended {savedCap.until ? new Date(savedCap.until * 1000).toLocaleString() : ""}. Choose a
              duration below to start a new one.
            </p>
          )}

          <div className="grid gap-x-6 gap-y-1 sm:grid-cols-2">
            <KitToggle checked={!!c.request} onChange={(b) => setC({ request: b })} label="Request bodies" help="What the visitor sent: form posts, JSON, uploads — as sent, up to the size kept per body." />
            <KitToggle checked={!!c.response} onChange={(b) => setC({ response: b })} label="Response bodies" help="What the site answered. Larger and more likely to hold personal data — capture only when needed." />
          </div>
          {!c.request && !c.response && <p className="mt-1 text-[12px] text-crit">Choose request bodies, response bodies, or both.</p>}

          <div className="mt-3">
            <div className="mb-1 text-[12px] text-muted">Methods captured</div>
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              {CAPTURE_METHODS.map((m) => (
                <label key={m} className="flex items-center gap-1.5 font-mono text-[12px]">
                  <input
                    type="checkbox"
                    checked={methods.includes(m)}
                    onChange={(e) => setC({ methods: e.target.checked ? [...methods, m] : methods.filter((x) => x !== m) })}
                  />
                  {m}
                </label>
              ))}
            </div>
            <span className="mt-1 block text-[11.5px] text-muted">GET bodies are rare; include GET for response bodies of pages or API reads.</span>
          </div>

          <div className="mt-3 grid gap-4 sm:grid-cols-3">
            <SelectField
              label="Capture for"
              value={dur}
              onChange={(h) => {
                setDur(h);
                setC({ until: h ? Math.floor(Date.now() / 1000) + Number(h) * 3600 : savedCap?.until });
              }}
              options={[
                { value: "", label: c.until ? `until ${new Date(c.until * 1000).toLocaleString()}` : "24 hours (default)" },
                // A saved capture that has already ended is re-started only by picking a duration.
                ...DURATIONS.map(([l, h]) => ({ value: String(h), label: `${l} from now` })),
              ]}
              help="Stops by itself then, on every node, whatever else happens. At most 7 days."
            />
            <NumberField
              label="Keep per body"
              unit="KB"
              value={c.max_kb ?? ""}
              placeholder="64"
              onChange={(v) => setC({ max_kb: parseInt(v, 10) || undefined })}
              help="The rest is counted, not stored. At most 1024."
            />
            <NumberField
              label="Keep captures for"
              unit="hours"
              value={c.retention_hours ?? ""}
              placeholder="24"
              onChange={(v) => setC({ retention_hours: parseInt(v, 10) || undefined })}
              help="Captured bodies are deleted after this. At most 168."
            />
          </div>

          <p className="mt-3 text-[11.5px] leading-relaxed text-warn">
            Bodies are recorded exactly as sent — passwords, tokens and personal data included. The capture records who
            turned it on and when, stops at its expiry, and its files are deleted after the keep period.
          </p>
        </div>
      )}
    </div>
  );
}
